/**
 * Ops Dashboard — Private Capital Infrastructure Monitoring
 * 
 * Real-time operational view of the multi-tenant Private Capital platform
 * (Honeyshares + REDEX tenants). Displays system health, transaction
 * throughput, error rates, reconciliation status, and provider info.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';
import {
  MonitorHeart,
  Speed,
  Warning,
  CheckCircle,
  Refresh,
  Storage,
  AccountBalance,
  Memory,
  Timer,
  PlayArrow,
  Assessment,
  Sync,
  Cloud,
  CloudOff,
  Circle,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import opsService, {
  HealthData,
  ThroughputData,
  ErrorRateData,
  ReconciliationRun,
  EnvironmentData,
  ProviderStatusData,
} from '../services/opsService';

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function StatusDot({ ok }: { ok: boolean }) {
  return (
    <Circle
      sx={{
        fontSize: 12,
        color: ok ? '#4caf50' : '#f44336',
        mr: 1,
        animation: ok ? undefined : 'pulse 1.5s infinite',
        '@keyframes pulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.4 },
        },
      }}
    />
  );
}

function centsToUsd(cents: number | string): string {
  const n = typeof cents === 'string' ? parseInt(cents, 10) : cents;
  return (n / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export default function OpsDashboardPage() {
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const [health, setHealth] = useState<HealthData | null>(null);
  const [throughput, setThroughput] = useState<ThroughputData | null>(null);
  const [errorRates, setErrorRates] = useState<ErrorRateData | null>(null);
  const [reconRuns, setReconRuns] = useState<ReconciliationRun[]>([]);
  const [environment, setEnvironment] = useState<EnvironmentData | null>(null);
  const [providerStatus, setProviderStatus] = useState<ProviderStatusData | null>(null);

  const [reconDialogOpen, setReconDialogOpen] = useState(false);
  const [reconLookback, setReconLookback] = useState(24);
  const [reconAutoFix, setReconAutoFix] = useState(false);
  const [reconRunning, setReconRunning] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.allSettled([
        opsService.getHealth(),
        opsService.getThroughput(),
        opsService.getErrorRates(),
        opsService.getReconciliationRuns(),
        opsService.getEnvironment(),
        opsService.getProviderStatus(),
      ]);

      if (results[0].status === 'fulfilled') setHealth(results[0].value.data.data);
      if (results[1].status === 'fulfilled') setThroughput(results[1].value.data.data);
      if (results[2].status === 'fulfilled') setErrorRates(results[2].value.data.data);
      if (results[3].status === 'fulfilled') setReconRuns(results[3].value.data.data.runs || []);
      if (results[4].status === 'fulfilled') setEnvironment(results[4].value.data.data);
      if (results[5].status === 'fulfilled') setProviderStatus(results[5].value.data.data);

      const failedCount = results.filter((r) => r.status === 'rejected').length;
      if (failedCount === results.length) {
        setError('Unable to reach Private Capital infrastructure API. Check connectivity and auth.');
      } else if (failedCount > 0) {
        setError(`${failedCount} of ${results.length} endpoints unreachable.`);
      }

      setLastRefresh(new Date());
    } catch (err: any) {
      setError(err.message || 'Failed to fetch ops data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 60000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  const handleRunRecon = async () => {
    setReconRunning(true);
    try {
      const res = await opsService.triggerReconciliation(reconLookback, reconAutoFix);
      setReconRuns((prev) => [res.data.data, ...prev]);
      setReconDialogOpen(false);
    } catch (err: any) {
      setError(`Reconciliation failed: ${err.message}`);
    } finally {
      setReconRunning(false);
    }
  };

  const checks = health?.checks;
  const mem = checks?.memory;
  const memoryPct = mem ? Math.round((mem.heapUsedMB / mem.heapTotalMB) * 100) : 0;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <MonitorHeart sx={{ fontSize: 36, color: theme.palette.primary.main }} />
            Ops Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Private Capital Infrastructure — Multi-Tenant Platform Monitoring
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Last refresh: {lastRefresh.toLocaleTimeString()}
          </Typography>
          <IconButton onClick={fetchAll} disabled={loading} size="small">
            <Refresh sx={{ animation: loading ? 'spin 1s linear infinite' : undefined, '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Status Banner */}
      {checks && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: checks.database.status === 'ok' ? 'rgba(76,175,80,0.08)' : 'rgba(244,67,54,0.08)' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
                <Storage sx={{ fontSize: 32, color: checks.database.status === 'ok' ? '#4caf50' : '#f44336' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">Databases</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {checks.database.status === 'ok' ? 'All Connected' : 'Degraded'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {checks.database.tenants || 1} tenant{(checks.database.tenants || 1) > 1 ? 's' : ''}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: checks.paymentProvider.status === 'ok' ? 'rgba(76,175,80,0.08)' : 'rgba(244,67,54,0.08)' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
                <AccountBalance sx={{ fontSize: 32, color: checks.paymentProvider.status === 'ok' ? '#4caf50' : '#f44336' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">Payment Provider</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {checks.paymentProvider.provider}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {checks.paymentProvider.environment} · {checks.paymentProvider.latencyMs}ms
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
                <Timer sx={{ fontSize: 32, color: theme.palette.primary.main }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">Uptime</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {formatUptime(checks.uptime.processSeconds)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Since last restart
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
                <Memory sx={{ fontSize: 32, color: memoryPct > 80 ? '#ff9800' : theme.palette.primary.main }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">Heap Memory</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {memoryPct}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={memoryPct}
                    sx={{
                      mt: 0.5,
                      height: 6,
                      borderRadius: 3,
                      bgcolor: 'rgba(0,0,0,0.08)',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: memoryPct > 80 ? '#ff9800' : theme.palette.primary.main,
                        borderRadius: 3,
                      },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {mem?.heapUsedMB}MB / {mem?.heapTotalMB}MB
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
        }}
      >
        <Tab icon={<Speed sx={{ fontSize: 18 }} />} iconPosition="start" label="Throughput" />
        <Tab icon={<Warning sx={{ fontSize: 18 }} />} iconPosition="start" label="Error Rates" />
        <Tab icon={<Sync sx={{ fontSize: 18 }} />} iconPosition="start" label="Reconciliation" />
        <Tab icon={<Assessment sx={{ fontSize: 18 }} />} iconPosition="start" label="Environment" />
      </Tabs>

      {/* Throughput Tab */}
      <TabPanel value={tab} index={0}>
        {!throughput && !loading && (
          <Alert severity="info">No throughput data available</Alert>
        )}
        {throughput && (
          <>
          {/* Aggregate totals header */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
            Platform Totals (All Tenants)
          </Typography>
          <Grid container spacing={2}>
            {/* ACH */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>ACH Transfers</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Period</TableCell>
                        <TableCell align="right">Count</TableCell>
                        <TableCell align="right">Volume</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>24 Hours</TableCell>
                        <TableCell align="right">{throughput.ach.ach_24h}</TableCell>
                        <TableCell align="right">{centsToUsd(throughput.ach.ach_volume_24h)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>7 Days</TableCell>
                        <TableCell align="right">{throughput.ach.ach_7d}</TableCell>
                        <TableCell align="right">{centsToUsd(throughput.ach.ach_volume_7d)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>30 Days</TableCell>
                        <TableCell align="right">{throughput.ach.ach_30d}</TableCell>
                        <TableCell align="right">{centsToUsd(throughput.ach.ach_volume_30d)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                    <Chip size="small" label={`${throughput.ach.ach_pending || 0} pending`} color="warning" variant="outlined" />
                    <Chip size="small" label={`${throughput.ach.ach_settled || 0} settled`} color="success" variant="outlined" />
                    <Chip size="small" label={`${throughput.ach.ach_failed || 0} failed`} color="error" variant="outlined" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Investments */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Investments</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Period</TableCell>
                        <TableCell align="right">Count</TableCell>
                        <TableCell align="right">Volume</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>24 Hours</TableCell>
                        <TableCell align="right">{throughput.investments.inv_24h}</TableCell>
                        <TableCell align="right">{centsToUsd(throughput.investments.inv_volume_24h)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>7 Days</TableCell>
                        <TableCell align="right">{throughput.investments.inv_7d}</TableCell>
                        <TableCell align="right">{centsToUsd(throughput.investments.inv_volume_7d)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>30 Days</TableCell>
                        <TableCell align="right">{throughput.investments.inv_30d}</TableCell>
                        <TableCell align="right">{centsToUsd(throughput.investments.inv_volume_30d)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Grid>

            {/* Engagements */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Engagement Transactions</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Period</TableCell>
                        <TableCell align="center">Count</TableCell>
                        <TableCell align="right">Volume</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>24 Hours</TableCell>
                        <TableCell align="center">{throughput.engagements.eng_24h}</TableCell>
                        <TableCell align="right">{centsToUsd(throughput.engagements.eng_volume_24h)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>7 Days</TableCell>
                        <TableCell align="center">{throughput.engagements.eng_7d}</TableCell>
                        <TableCell align="right">{centsToUsd(throughput.engagements.eng_volume_7d)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>30 Days</TableCell>
                        <TableCell align="center">{throughput.engagements.eng_30d}</TableCell>
                        <TableCell align="right">{centsToUsd(throughput.engagements.eng_volume_30d)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip size="small" label={`${throughput.engagements.eng_pending || 0} pending`} variant="outlined" color="warning" />
                    <Chip size="small" label={`${throughput.engagements.eng_settled || 0} settled`} variant="outlined" color="success" />
                    <Chip size="small" label={`${throughput.engagements.eng_failed || 0} failed`} variant="outlined" color="error" />
                    <Chip size="small" label={`${throughput.engagements.eng_cancelled || 0} cancelled`} variant="outlined" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Users */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Users</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Period</TableCell>
                        <TableCell align="right">New</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>24 Hours</TableCell>
                        <TableCell align="right">{throughput.users.users_24h}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>7 Days</TableCell>
                        <TableCell align="right">{throughput.users.users_7d}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>30 Days</TableCell>
                        <TableCell align="right">{throughput.users.users_30d}</TableCell>
                      </TableRow>
                      <TableRow sx={{ fontWeight: 700 }}>
                        <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{throughput.users.users_total}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Per-Tenant breakdown */}
          {throughput.byTenant && throughput.tenants && (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3, mb: 1.5 }}>
                Per-Tenant Breakdown
              </Typography>
              <Grid container spacing={2}>
                {throughput.tenants.map((t: any) => (
                  <Grid item xs={12} md={6} key={t.id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                          {t.label}
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Metric</TableCell>
                              <TableCell align="right">30d Count</TableCell>
                              <TableCell align="right">30d Volume</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>ACH</TableCell>
                              <TableCell align="right">{throughput.byTenant.ach?.[t.id]?.ach_30d || 0}</TableCell>
                              <TableCell align="right">{centsToUsd(throughput.byTenant.ach?.[t.id]?.ach_volume_30d || 0)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Investments</TableCell>
                              <TableCell align="right">{throughput.byTenant.investments?.[t.id]?.inv_30d || 0}</TableCell>
                              <TableCell align="right">{centsToUsd(throughput.byTenant.investments?.[t.id]?.inv_volume_30d || 0)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Engagements</TableCell>
                              <TableCell align="right">{throughput.byTenant.engagements?.[t.id]?.eng_30d || 0}</TableCell>
                              <TableCell align="right">{centsToUsd(throughput.byTenant.engagements?.[t.id]?.eng_volume_30d || 0)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Users</TableCell>
                              <TableCell align="right">{throughput.byTenant.users?.[t.id]?.users_30d || 0}</TableCell>
                              <TableCell align="right">{throughput.byTenant.users?.[t.id]?.users_total || 0} total</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
          </>
        )}
      </TabPanel>

      {/* Error Rates Tab */}
      <TabPanel value={tab} index={1}>
        {errorRates && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>ACH Failure Rate (30d)</Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: parseFloat(errorRates.ach.failure_rate_pct) > 5 ? '#f44336' : '#4caf50' }}>
                    {errorRates.ach.failure_rate_pct}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {errorRates.ach.failed} failed of {errorRates.ach.total} total
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Reconciliation (7d)</Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {errorRates.reconciliation.total_mismatches}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    mismatches · {errorRates.reconciliation.total_runs} runs · {errorRates.reconciliation.total_synced} synced
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Admin Audit Actions (7d)</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {errorRates.auditActivity.map((a, i) => (
                      <Chip key={i} size="small" label={`${a.action}: ${a.count}`} variant="outlined" />
                    ))}
                    {errorRates.auditActivity.length === 0 && (
                      <Typography variant="body2" color="text.secondary">No audit activity</Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Recent Failures */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Recent ACH Failures (7d)</Typography>
                  {errorRates.recentFailures.length === 0 ? (
                    <Alert severity="success">No recent failures</Alert>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {errorRates.recentFailures.map((f, i) => (
                            <TableRow key={i}>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                {String(f.id).substring(0, 8)}...
                              </TableCell>
                              <TableCell>
                                <Chip size="small" label={f.type} variant="outlined" />
                              </TableCell>
                              <TableCell>
                                <Chip size="small" label={f.status} color="error" />
                              </TableCell>
                              <TableCell>{centsToUsd(f.amount_cents)}</TableCell>
                              <TableCell>{new Date(f.created_at).toLocaleString()}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
        {!errorRates && !loading && <Alert severity="info">No error rate data available</Alert>}
      </TabPanel>

      {/* Reconciliation Tab */}
      <TabPanel value={tab} index={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Reconciliation Runs</Typography>
          <Button
            variant="contained"
            startIcon={<PlayArrow />}
            onClick={() => setReconDialogOpen(true)}
            size="small"
          >
            Run Reconciliation
          </Button>
        </Box>

        {reconRuns.length === 0 && !loading ? (
          <Alert severity="info">No reconciliation runs yet. Click "Run Reconciliation" to start.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="center">Checked</TableCell>
                  <TableCell align="center">Mismatches</TableCell>
                  <TableCell align="center">Synced</TableCell>
                  <TableCell align="center">Errors</TableCell>
                  <TableCell align="center">Auto-Fix</TableCell>
                  <TableCell align="right">Duration</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reconRuns.map((run) => (
                  <TableRow key={run.id}>
                    <TableCell>{new Date(run.created_at).toLocaleString()}</TableCell>
                    <TableCell align="center">{run.checked}</TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={run.mismatches}
                        color={run.mismatches > 0 ? 'warning' : 'success'}
                      />
                    </TableCell>
                    <TableCell align="center">{run.synced}</TableCell>
                    <TableCell align="center">
                      {run.errors > 0 ? (
                        <Chip size="small" label={run.errors} color="error" />
                      ) : (
                        run.errors
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {run.auto_fix ? (
                        <CheckCircle sx={{ fontSize: 18, color: '#4caf50' }} />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                      {run.duration_ms}ms
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Run Reconciliation Dialog */}
        <Dialog open={reconDialogOpen} onClose={() => setReconDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Run Reconciliation</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Reconcile internal ACH transfer states against the payment provider.
            </Typography>
            <TextField
              label="Lookback Hours"
              type="number"
              value={reconLookback}
              onChange={(e) => setReconLookback(Number(e.target.value))}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              inputProps={{ min: 1, max: 720 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={reconAutoFix}
                  onChange={(e) => setReconAutoFix(e.target.checked)}
                  color="warning"
                />
              }
              label="Auto-fix mismatches"
            />
            {reconAutoFix && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Auto-fix will update internal states to match the provider. Review results carefully.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReconDialogOpen(false)} disabled={reconRunning}>Cancel</Button>
            <Button
              onClick={handleRunRecon}
              variant="contained"
              disabled={reconRunning}
              startIcon={reconRunning ? <CircularProgress size={16} /> : <PlayArrow />}
            >
              {reconRunning ? 'Running...' : 'Run'}
            </Button>
          </DialogActions>
        </Dialog>
      </TabPanel>

      {/* Environment Tab */}
      <TabPanel value={tab} index={3}>
        <Grid container spacing={2}>
          {environment && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Assessment sx={{ fontSize: 20 }} /> Environment
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Environment</Typography>
                      <Chip size="small" label={environment.environment} color="primary" />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Tenants</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {Array.isArray(environment.tenants)
                          ? environment.tenants.map((t: any) => t.label).join(', ')
                          : environment.tenant || '—'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Payment Provider</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{environment.payments.provider}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Sandbox Mode</Typography>
                      <Chip
                        size="small"
                        label={environment.payments.sandbox ? 'Sandbox' : 'Production'}
                        color={environment.payments.sandbox ? 'warning' : 'success'}
                      />
                    </Box>
                  </Box>

                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>Feature Flags</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {Object.entries(environment.features).map(([key, enabled]) => (
                      <Chip
                        key={key}
                        size="small"
                        icon={<StatusDot ok={enabled} />}
                        label={key}
                        variant="outlined"
                        sx={{ borderColor: enabled ? 'rgba(76,175,80,0.3)' : 'rgba(244,67,54,0.3)' }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {providerStatus && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    {providerStatus.connected ? (
                      <Cloud sx={{ fontSize: 20, color: '#4caf50' }} />
                    ) : (
                      <CloudOff sx={{ fontSize: 20, color: '#f44336' }} />
                    )}
                    Payment Provider
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Provider</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{providerStatus.provider}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Status</Typography>
                      <Chip
                        size="small"
                        label={providerStatus.connected ? 'Connected' : 'Disconnected'}
                        color={providerStatus.connected ? 'success' : 'error'}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Mode</Typography>
                      <Chip
                        size="small"
                        label={providerStatus.environment === 'production' ? 'Live' : 'Sandbox'}
                        color={providerStatus.environment === 'production' ? 'success' : 'warning'}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Latency</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{providerStatus.latencyMs}ms</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Last Check</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {providerStatus.lastCheck ? new Date(providerStatus.lastCheck).toLocaleString() : '—'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {!environment && !providerStatus && !loading && (
            <Grid item xs={12}>
              <Alert severity="info">No environment data available</Alert>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* Loading overlay for initial load */}
      {loading && !health && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            Connecting to Private Capital infrastructure...
          </Typography>
        </Box>
      )}
    </Box>
  );
}
